import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { fetchAndParseCSV, fetchAndParseXLSX } from "../../../shared/csvParser";
import { QIList } from "@qinvent/react-kit";
import "./PrintPreviewPage.scss";
import sprite from "../../../assets/icons.svg";

// Helper function to determine cell class based on content
const getCellClass = (header, content = "") => {
  const headerLower = (header || "").toLowerCase();
  const classes = [];

  // Numeric columns - only for pure numeric data, not IDs
  if (
    (headerLower.includes("count") ||
      headerLower.includes("number") ||
      headerLower.includes("total") ||
      headerLower.includes("amount") ||
      headerLower.includes("distance") ||
      headerLower.includes("duration")) &&
    !isNaN(parseFloat(content)) &&
    isFinite(content)
  ) {
    classes.push("numeric");
  }

  // Text columns that should wrap (not truncate)
  if (
    headerLower.includes("name") ||
    headerLower.includes("type") ||
    headerLower.includes("status") ||
    headerLower.includes("category") ||
    headerLower.includes("plate") ||
    headerLower.includes("description") ||
    headerLower.includes("address") ||
    headerLower.includes("location")
  ) {
    classes.push("wrap");
  }

  // Date/Time columns
  if (
    headerLower.includes("date") ||
    headerLower.includes("time") ||
    headerLower.includes("timestamp") ||
    headerLower.includes("day")
  ) {
    classes.push("wrap");
  }

  // Raw data / base64 / long content columns should truncate
  if (headerLower === "data" || headerLower.includes("packet") || headerLower.includes("imei")) {
    classes.push("truncate");
  }

  // Long ID columns can truncate (only if not already wrapping)
  if (
    !classes.includes("wrap") &&
    !classes.includes("truncate") &&
    (headerLower.includes("device id") ||
      (headerLower.includes("id") && content && content.length > 20))
  ) {
    classes.push("truncate");
  }

  return classes.join(" ");
};

export const PrintPreviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnItems, setSelectedColumnItems] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const loadReport = async () => {
      try {
        setLoading(true);

        // Get the report URL from query parameters
        const params = new URLSearchParams(location.search);
        const reportUrl = params.get("url");

        if (!reportUrl) {
          throw new Error("No report URL provided");
        }

        const data = await (params.get("format") === "excel"
          ? fetchAndParseXLSX(reportUrl)
          : fetchAndParseCSV(reportUrl));
        setReportData(data);

        // Initialize selected columns
        if (data && data.tableData && data.tableData.headers) {
          setSelectedColumns(data.tableData.headers);
          setSelectedColumnItems(data.tableData.headers);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [location]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  // Transform report data to QIList format
  const {
    headers,
    data: listData,
    columnData,
  } = useMemo(() => {
    if (!reportData || !reportData.tableData) return { headers: [], data: [], columnData: [] };

    console.log(reportData);

    const headers = reportData.tableData.headers.map((header) => {
      const isWideColumn = /address|location/i.test(header);
      return {
        label: header,
        key: header,
        className: `${getCellClass(header)} ${isWideColumn ? "tracking-wide-column" : ""}`.trim(),
        maxWidth: isWideColumn ? "500px" : "400px",
        searchable: true,
        sortable: false,
      };
    });

    const data = reportData.tableData.rows.map((row, rowIndex) => {
      const rowObj = { id: rowIndex };
      reportData.tableData.headers.forEach((header, colIndex) => {
        rowObj[header] = row[colIndex];
      });
      return rowObj;
    });

    const columnData = headers.map((header, index) => ({
      name: header.label,
      id: header.key,
      enabled: true,
      key: header.key,
    }));

    return { headers, data, columnData };
  }, [reportData]);

  if (loading) {
    return (
      <div className="print-preview-page">
        <div className="print-preview-loading">
          <div className="spinner"></div>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-preview-page">
        <div className="print-preview-error">
          <h2>Error Loading Report</h2>
          <p>{error}</p>
          <button onClick={handleClose} className="btn-close-error">
            Close Window
          </button>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  const { metadata, tableData } = reportData;

  return (
    <div className="print-preview-page">
      {/* Header with branding and action buttons */}
      <div className="print-preview-header no-print">
        <div className="header-left">
          <h2 className="app-title">Tracking Application</h2>
        </div>
        <div className="header-right">
          <div className="header-actions">
            <button onClick={handlePrint} className="btn-print">
              <svg
                className="icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 14h12v8H6v-8z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Print
            </button>
            <button onClick={handleClose} className="btn-close">
              Close
            </button>
          </div>
        </div>
      </div>

      <div className="print-content">
        <div className="report-header">
          <h1 className="report-title">{metadata["Report Name"] || "Report"}</h1>
        </div>

        <div className="report-metadata">
          <div className="metadata-grid">
            {Object.entries(metadata).map(([key, value]) => (
              <div key={key} className="metadata-item">
                <span className="metadata-label">{key}:</span>
                <span className="metadata-value">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {listData.length > 0 ? (
          <div className="report-table-section">
            <QIList
              columns={headers.filter((col) => selectedColumns.includes(col.key))}
              data={listData}
              className="print-preview-report"
              detailedView={false}
              detailedData={listData}
              sortable={true}
              searchable={true}
              showDetails={true}
              columnData={columnData}
              columnConfig={true}
              selectedColumns={selectedColumns}
              setSelectedColumns={setSelectedColumns}
              scrollbarHeight="100%"
              headers={headers}
              resizableColumns
              alwaysShowResizeHandles
            />
          </div>
        ) : (
          <div className="no-data">
            <p>No data available</p>
          </div>
        )}

        <div className="report-footer">
          <p>Generated on {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};
